// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useRef, useState } from 'react';

import { BffWebSocketClient, UserCreatedEvent, getSharedBffWebSocket } from '@/services/bffWebSocket';
import { useI18n } from '@/i18n';

const MIN_PASSWORD_LENGTH = 6;
const REGISTER_REQUEST_TIMEOUT_MS = 15000;

type UseRegisterUserParams = {
  apiBaseUrl: string;
  isFocused: boolean;
  displayName: string;
  fullName: string;
  email: string;
  password: string;
  onReset?: () => void;
  onSuccess?: () => void;
};

export const useRegisterUser = ({
  apiBaseUrl,
  isFocused,
  displayName,
  fullName,
  email,
  password,
  onReset,
  onSuccess,
}: UseRegisterUserParams) => {
  const { t } = useI18n();
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [socketState, setSocketState] = useState<'connected' | 'disconnected'>('disconnected');
  const requestIdRef = useRef<string | null>(null);
  const socketRef = useRef<BffWebSocketClient | null>(null);
  const pendingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onResetRef = useRef(onReset);
  const onSuccessRef = useRef(onSuccess);

  useEffect(() => {
    onResetRef.current = onReset;
  }, [onReset]);

  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);

  const clearPendingTimeout = useCallback(() => {
    if (!pendingTimeoutRef.current) return;
    clearTimeout(pendingTimeoutRef.current);
    pendingTimeoutRef.current = null;
  }, []);

  const startPendingTimeout = useCallback(() => {
    clearPendingTimeout();
    pendingTimeoutRef.current = setTimeout(() => {
      if (!requestIdRef.current) return;
      console.warn('[register] user.create timed out', { requestId: requestIdRef.current });
      requestIdRef.current = null;
      setLoading(false);
      setStatus(null);
      setError(t('register.createTimeout'));
    }, REGISTER_REQUEST_TIMEOUT_MS);
  }, [clearPendingTimeout, t]);

  useEffect(() => {
    const client = getSharedBffWebSocket(apiBaseUrl);
    socketRef.current = client;
    client.setAnonymousReadOnly(true);
    const handleInboundMessage = (message: UserCreatedEvent) => {
      if (message?.type !== 'user.created') return;
      if (!requestIdRef.current || message.requestId !== requestIdRef.current) return;
      console.info('[register] user.created received', {
        requestId: message.requestId,
        queued: message.queued,
        success: message.success,
        error: message.error,
      });

      if (message.queued) {
        setStatus(t('register.statusQueued'));
        setError(null);
        setLoading(true);
        startPendingTimeout();
        return;
      }

      clearPendingTimeout();
      setLoading(false);
      if (message.success === false) {
        const errorKey = message.error;
        if (errorKey === 'user.exists' || errorKey === 'user.email.exists') {
          setError(t('register.userExists'));
        } else {
          setError(t('register.createFailed'));
        }
        setStatus(null);
        requestIdRef.current = null;
        return;
      }

      setStatus(t('register.statusSuccess'));
      setError(null);
      onResetRef.current?.();
      onSuccessRef.current?.();
      requestIdRef.current = null;
    };

    const unsubscribe = client.onMessage((msg) => handleInboundMessage(msg as UserCreatedEvent));
    const onConn = client.onConnectionChange((state) => {
      setSocketState(state);
      if (state === 'disconnected' && requestIdRef.current) {
        console.warn('[register] BFF disconnected while user.create pending', { requestId: requestIdRef.current });
        requestIdRef.current = null;
        clearPendingTimeout();
        setLoading(false);
        setStatus(null);
        setError(t('register.connectionMissing'));
      }
    });
    if (isFocused) {
      client.setSubscriptionContext({ roundId: null, tourId: null });
    }
    return () => {
      unsubscribe();
      onConn();
      clearPendingTimeout();
      const path = typeof window !== 'undefined' ? window.location?.pathname ?? '' : '';
      if (!path.startsWith('/register')) {
        client.setAnonymousReadOnly(false);
      }
    };
  }, [apiBaseUrl, clearPendingTimeout, isFocused, startPendingTimeout, t]);

  const sendCreateRequest = useCallback(() => {
    if (!email.trim() || !displayName.trim() || !fullName.trim() || !password) {
      setError(t('register.validationMissing'));
      return;
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(t('register.validationPasswordTooShort', { min: MIN_PASSWORD_LENGTH }));
      return;
    }
    if (!socketRef.current) {
      setError(t('register.connectionMissing'));
      return;
    }

    const requestId = generateRequestId();
    requestIdRef.current = requestId;
    setStatus(t('register.statusQueued'));
    setError(null);
    setLoading(true);
    startPendingTimeout();
    console.info('[register] sending user.create', {
      requestId,
      email: email.trim(),
      displayName: displayName.trim(),
      fullName: fullName.trim(),
    });
    const sent = socketRef.current.sendUserCreate({
      displayName: displayName.trim(),
      fullName: fullName.trim(),
      password,
      email: email.trim(),
      requestId,
    });
    if (!sent) {
      console.warn('[register] user.create send failed', { requestId });
      requestIdRef.current = null;
      clearPendingTimeout();
      setLoading(false);
      setStatus(null);
      setError(t('register.connectionMissing'));
      return;
    }
  }, [clearPendingTimeout, displayName, email, fullName, password, startPendingTimeout, t]);

  return {
    status,
    error,
    loading,
    socketState,
    sendCreateRequest,
  };
};

const generateRequestId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    const uuid = (crypto as { randomUUID?: () => string }).randomUUID?.();
    if (uuid) return uuid;
  }
  return `req-${Date.now()}`;
};
